/**
 * Sanitize NDC input: strip everything except digits, cap at 11.
 * Use this as the onChange handler so the user types raw digits freely.
 *
 * @example
 * sanitizeNDCInput("1234-5678-901") // "12345678901"
 * sanitizeNDCInput("00781 1506 10") // "00781150610"
 * sanitizeNDCInput("1234567890123") // "12345678901" (capped at 11)
 */
export function sanitizeNDCInput(input: string): string {
  return input.replace(/\D/g, "").slice(0, 11);
}

/**
 * Format an 11-digit NDC string to the standard 5-4-2 display format: XXXXX-XXXX-XX.
 * Returns the raw input unchanged if it doesn't have exactly 11 digits.
 *
 * @example
 * formatNDC("12345678901") // "12345-6789-01"
 * formatNDC("12345-6789-01") // "12345-6789-01"
 * formatNDC("1234567890") // "1234567890" (10 digits – returned as-is)
 */
export function formatNDC(input: string): string {
  const numbers = input.replace(/\D/g, "");

  if (numbers.length === 11) {
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 9)}-${numbers.slice(9, 11)}`;
  }

  // Not 11 digits – return raw digits (no partial-dash insertion)
  return numbers;
}

/**
 * Validate if a string is a properly formatted 11-digit NDC (5-4-2 with dashes).
 *
 * @example
 * isValidNDC("12345-6789-01") // true
 * isValidNDC("12345678901")   // false (no dashes)
 */
export function isValidNDC(ndc: string): boolean {
  return /^\d{5}-\d{4}-\d{2}$/.test(ndc);
}

/**
 * Extract raw NDC digits without dashes.
 *
 * @example
 * getRawNDC("12345-6789-01") // "12345678901"
 * getRawNDC("12345678901")   // "12345678901"
 */
export function getRawNDC(ndc: string): string {
  return ndc.replace(/\D/g, "");
}

/**
 * Check if a value contains 10 or 11 digits, meaning it could be a valid NDC
 * suitable for API lookup. Works with raw digits or formatted input.
 *
 * @example
 * isNDCLookupLength("12345678901")   // true  (11 digits)
 * isNDCLookupLength("1234567890")    // true  (10 digits)
 * isNDCLookupLength("12345-6789-01") // true  (11 digits with dashes)
 * isNDCLookupLength("123456789")     // false (9 digits)
 */
export function isNDCLookupLength(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  return digits.length === 10 || digits.length === 11;
}

/**
 * Given exactly 10 bare digits, return the three possible 11-digit NDCs
 * (formatted as 5-4-2 with dashes) by adding a leading zero to each segment.
 *
 * The three 10-digit NDC formats and their 11-digit expansions:
 *   4-4-2  →  add 0 to labeler  →  05-4-2
 *   5-3-2  →  add 0 to product  →  5-04-2
 *   5-4-1  →  add 0 to package  →  5-4-02
 *
 * @example
 * expandNDC10to11("0781150610")
 * // ["00781-1506-10", "07811-0506-10", "07811-5061-00"]
 */
export function expandNDC10to11(digits: string): string[] {
  const clean = digits.replace(/\D/g, "");
  if (clean.length !== 10) return [];

  return [
    // 4-4-2 → prepend 0 to labeler (first 4 digits)
    `0${clean.slice(0, 4)}-${clean.slice(4, 8)}-${clean.slice(8, 10)}`,
    // 5-3-2 → prepend 0 to product (digits 5-7)
    `${clean.slice(0, 5)}-0${clean.slice(5, 8)}-${clean.slice(8, 10)}`,
    // 5-4-1 → prepend 0 to package (last digit)
    `${clean.slice(0, 5)}-${clean.slice(5, 9)}-0${clean.slice(9, 10)}`,
  ];
}
